import React, { FC, useState } from 'react';
import { Button, Loader, Popover, ScrollArea, Text, TextInput, Tooltip, UnstyledButton } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import { IconSearch, IconTemplate } from '@tabler/icons-react';
import Fetch from '@/utils/Fetch';
import * as Enums from '@/utils/enums';

const PAGE_SIZE = 50;

// "Use a template" link with a searchable list of the account's task templates for the module
const TaskTemplatePicker: FC<{ module: any; disabled?: boolean; onPick: (template: any) => void }> = ({ module, disabled, onPick }) => {
    const [opened, setOpened] = useState(false);
    const [search, setSearch] = useState('');
    const [active, setActive] = useState(0);
    const [debounced] = useDebouncedValue(search.trim(), 250);

    const { data, isFetching } = useQuery(['taskTemplates', module, debounced], async () => {
        const result = await Fetch.post({
            url: '/TaskTemplate/GetTaskTemplates',
            params: {
                ModuleList: [Enums.getEnumStringValue(Enums.Module, module == Enums.Module.JobCard ? Enums.Module.JobCard : Enums.Module.Query)],
                SearchPhrase: debounced,
                PageIndex: 0,
                PageSize: PAGE_SIZE,
            },
        });
        return { templates: (result?.Results ?? []) as any[], total: (result?.TotalResults ?? 0) as number };
    }, { keepPreviousData: true, staleTime: 60_000 });

    // known once the list has loaded: the account has no task templates at all
    const noTemplates = !!data && data.total === 0 && !debounced;

    const templates = data?.templates ?? [];
    const pick = (template: any) => {
        if (!template) return;
        setOpened(false);
        onPick(template);
    };

    return (
        <Popover opened={opened && !noTemplates} onChange={setOpened} position={'top-start'} width={300} shadow={'md'} withinPortal trapFocus
                 onOpen={() => { setSearch(''); setActive(0); }}>
            <Popover.Target>
                {/* the span lets the tooltip work on a disabled button */}
                <span>
                    <Tooltip label={'No task templates have been created yet'} disabled={!noTemplates} withArrow>
                        <span>
                            <Button variant={'subtle'} size={'compact-sm'} leftSection={<IconTemplate size={16} />} disabled={disabled || noTemplates}
                                    aria-haspopup={'listbox'} aria-expanded={opened} onClick={() => setOpened(o => !o)}>
                                Use a template
                            </Button>
                        </span>
                    </Tooltip>
                </span>
            </Popover.Target>
            <Popover.Dropdown p={'xs'}>
                <TextInput
                    data-autofocus
                    data-mantine-stop-propagation
                    size={'xs'}
                    placeholder={'Search templates'}
                    aria-label={'Search templates'}
                    leftSection={<IconSearch size={14} />}
                    rightSection={isFetching ? <Loader size={12} /> : null}
                    value={search}
                    onChange={e => { setSearch(e.currentTarget.value); setActive(0); }}
                    onKeyDown={e => {
                        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                            e.preventDefault();
                            if (templates.length) setActive(a => (a + (e.key === 'ArrowDown' ? 1 : -1) + templates.length) % templates.length);
                        } else if (e.key === 'Enter') {
                            e.preventDefault();
                            pick(templates[active]);
                        } else if (e.key === 'Escape') {
                            // closes the list, not the dialog behind it
                            e.preventDefault();
                            e.stopPropagation();
                            setOpened(false);
                        }
                    }}
                />
                <ScrollArea.Autosize mah={220} mt={6} type={'auto'}>
                    <div role={'listbox'} aria-label={'Task templates'}>
                        {templates.map((template, i) =>
                            <UnstyledButton key={template.ID} role={'option'} aria-selected={i === active} w={'100%'} px={'xs'} py={6}
                                            style={{ borderRadius: 4, background: i === active ? 'var(--mantine-color-scBlue-0)' : undefined }}
                                            onMouseEnter={() => setActive(i)} onClick={() => pick(template)}>
                                <Text size={'sm'} truncate>{template.Name}</Text>
                            </UnstyledButton>
                        )}
                        {templates.length === 0 && !isFetching && <Text size={'sm'} c={'dimmed'} px={'xs'} py={6}>No templates match</Text>}
                    </div>
                </ScrollArea.Autosize>
            </Popover.Dropdown>
        </Popover>
    );
};

export default TaskTemplatePicker;
